import type { CalendarDate } from '@domain/value-objects/CalendarDate';
import type { DateRange } from '@domain/value-objects/DateRange';
import type { HourWindow } from '@domain/entities/PermissionRequest';
import type { IncapacityStatus, PermissionRequestStatus, RequestStatus } from '@domain/vocabulary';

/**
 * Presentation formatting: dates, ranges and status colours.
 *
 * Kept out of the domain because these are decisions about how something *reads* in
 * Spanish, not about what it means. Kept out of the components because the same
 * range has to render identically on a card, in a calendar and in an email preview.
 */

/** Exported because the date boxes on the request and incapacity rows use it. */
export const MONTHS_SHORT = [
  'ene',
  'feb',
  'mar',
  'abr',
  'may',
  'jun',
  'jul',
  'ago',
  'sep',
  'oct',
  'nov',
  'dic',
] as const;

export const MONTHS_LONG = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
] as const;

export const WEEKDAYS_SHORT = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'] as const;

/** `10 ago`, matching the handoff's compact date. */
export const formatDay = (date: CalendarDate): string =>
  `${String(date.day)} ${MONTHS_SHORT[date.month - 1] ?? ''}`;

/**
 * `15 jul 2027` — the compact date with its year.
 *
 * For anything more than a few weeks out, `formatDay` alone is ambiguous: a
 * vesting date reading «se liberan el 15 jul» leaves the reader guessing which
 * July, and it is the one date a new hire is counting down to.
 */
export const formatDayWithYear = (date: CalendarDate): string =>
  `${formatDay(date)} ${String(date.year)}`;

/** `agosto 2026`, for a calendar header. */
export const formatMonth = (date: CalendarDate): string =>
  `${MONTHS_LONG[date.month - 1] ?? ''} ${String(date.year)}`;

/**
 * `10 ago – 21 ago 2026`, matching the handoff's explicit month on both dates.
 */
export const formatRange = (start: CalendarDate, end: CalendarDate): string => {
  if (start.equals(end)) {
    return `${formatDay(start)} ${String(start.year)}`;
  }

  if (start.year !== end.year) {
    return `${formatDay(start)} ${String(start.year)} – ${formatDay(end)} ${String(end.year)}`;
  }

  return `${formatDay(start)} – ${formatDay(end)} ${String(end.year)}`;
};

export const formatDateRange = (range: DateRange): string => formatRange(range.start, range.end);

/**
 * `09:00–13:00`, o `sale 2 h antes · no regresa` cuando el permiso no vuelve.
 *
 * Aquí y no en cada pantalla porque son tres —Mis ausencias, Aprobaciones y el
 * resumen del asistente— y un permiso sin regreso que en una de ellas se leyera
 * como un rango sería justo el dato que hay que no equivocar: es la diferencia
 * entre alguien que vuelve a las 13:00 y alguien que ya no vuelve hoy.
 *
 * La tercera rama es la forma antigua —hora de salida sin regreso—, que ya no se
 * escribe pero está en los documentos del 18 de agosto de 2026. Ver `HourWindow`.
 */
export const formatHourWindow = (window: HourWindow): string => {
  if (window.earlyHours !== null) {
    return `sale ${String(window.earlyHours)} h antes · no regresa`;
  }
  if (window.from === null) return 'Sin horario';
  if (window.to === null) return `desde las ${window.from} · sin regreso`;
  return `${window.from}–${window.to}`;
};

/** `10 días hábiles`, with the singular handled. */
export const formatBusinessDays = (count: number): string =>
  `${String(count)} ${count === 1 ? 'día hábil' : 'días hábiles'}`;

export const formatNaturalDays = (count: number): string =>
  `${String(count)} ${count === 1 ? 'día natural' : 'días naturales'}`;

/**
 * Status chip colours, transcribed from the handoff's `TAGS` map.
 *
 * `Desconocido` — the value an older client decodes a newer status into — gets the
 * neutral grey rather than being absent from the map. That is the point of the
 * fallback: a status from the future renders as a plain chip instead of an
 * unstyled one.
 */
export interface ChipColors {
  readonly background: string;
  readonly foreground: string;
}

const NEUTRAL: ChipColors = {
  background: 'var(--color-hairline)',
  foreground: 'var(--color-text-tertiary-alt)',
};

const REQUEST_STATUS_COLORS: Readonly<Record<RequestStatus, ChipColors>> = {
  Aprobada: { background: 'var(--color-success-bg)', foreground: 'var(--color-success-ink)' },
  'Por revisar': { background: 'var(--color-violet-bg)', foreground: 'var(--color-violet-ink)' },
  'En revisión': { background: 'var(--color-warning-bg)', foreground: 'var(--color-warning-ink-alt)' },
  'Visto bueno': { background: 'var(--color-brand-bg)', foreground: 'var(--color-brand-ink)' },
  Rechazada: { background: 'var(--color-danger-bg)', foreground: 'var(--color-danger-ink-alt)' },
  Cancelada: NEUTRAL,
  Tomada: { background: 'var(--color-brand-bg)', foreground: 'var(--color-brand-strong)' },
  Borrador: NEUTRAL,
  Desconocido: NEUTRAL,
};

export const statusChipColors = (status: RequestStatus): ChipColors =>
  REQUEST_STATUS_COLORS[status] ?? NEUTRAL;

/** Strong status accent used by the prototype's request date cells and trail. */
export const statusAccentColor = (status: RequestStatus): string => {
  switch (status) {
    case 'Aprobada':
      return 'var(--color-success)';
    case 'En revisión':
    case 'Por revisar':
      return 'var(--color-warning)';
    case 'Rechazada':
      return 'var(--color-danger-bright)';
    case 'Visto bueno':
    case 'Tomada':
      return 'var(--color-brand)';
    default:
      return 'var(--color-text-tertiary)';
  }
};

const PERMISSION_STATUS_COLORS: Readonly<Record<PermissionRequestStatus, ChipColors>> = {
  Aprobado: { background: 'var(--color-success-bg)', foreground: 'var(--color-success-ink)' },
  'Por aprobar': { background: 'var(--color-warning-bg)', foreground: 'var(--color-warning-ink-alt)' },
  Rechazado: { background: 'var(--color-danger-bg)', foreground: 'var(--color-danger-ink-alt)' },
  Cancelado: NEUTRAL,
  Desconocido: NEUTRAL,
};

export const permissionStatusColors = (status: PermissionRequestStatus): ChipColors =>
  PERMISSION_STATUS_COLORS[status] ?? NEUTRAL;

const INCAPACITY_STATUS_COLORS: Readonly<Record<IncapacityStatus, ChipColors>> = {
  Validada: { background: 'var(--color-success-bg)', foreground: 'var(--color-success-ink)' },
  'Por validar': { background: 'var(--color-warning-bg)', foreground: 'var(--color-warning-ink-alt)' },
  Observada: { background: 'var(--color-danger-bg)', foreground: 'var(--color-danger-ink-alt)' },
  Programada: { background: 'var(--color-brand-bg)', foreground: 'var(--color-brand-ink)' },
  // Neutra como el permiso cancelado: retirada no es rechazada.
  Cancelada: NEUTRAL,
  Desconocido: NEUTRAL,
};

export const incapacityStatusColors = (status: IncapacityStatus): ChipColors =>
  INCAPACITY_STATUS_COLORS[status] ?? NEUTRAL;

/** `+52 55 4821 7730` left as stored — the handoff shows numbers verbatim. */
export const formatPhone = (raw: string): string => raw.trim();

/** A count with its noun, for "3 personas" / "1 persona". */
export const pluralize = (count: number, singular: string, plural: string): string =>
  `${String(count)} ${count === 1 ? singular : plural}`;

/**
 * The reason a mutation failed, appended to the screen's own sentence.
 *
 * Written because «No se pudo cambiar el estado del área.» on its own is
 * unactionable — it is the same sentence whether the network dropped, the
 * permission matrix refuses, or the area still has people in it. The server
 * already writes the specific answer («El área tiene 3 persona(s) activa(s).
 * Reasígnalas antes de desactivarla.»); this puts it in front of the person
 * instead of leaving it in the console.
 *
 * Returns an empty string when there is nothing worth adding, so the caller can
 * concatenate unconditionally.
 */
export const reasonFor = (error: { readonly kind: string } & Record<string, unknown>): string => {
  const detail = typeof error['detail'] === 'string' ? error['detail'] : '';
  const what = typeof error['what'] === 'string' ? error['what'] : '';

  switch (error.kind) {
    case 'permission-denied':
      return what.length > 0 ? ` ${what}` : ' Tu perfil no tiene permiso para esta acción.';
    case 'conflict':
    case 'not-found':
    case 'invalid':
      return what.length > 0 ? ` ${what}` : '';
    case 'unavailable':
      return detail.length > 0 ? ` ${detail}` : ' Revisa tu conexión e intenta de nuevo.';
    default:
      return '';
  }
};

/**
 * Las mayúsculas que un título automático no puede adivinar.
 *
 * Sacadas del catálogo real, no imaginadas: son las palabras de los 320 puestos
 * de producción que no se reconstruyen poniendo la primera letra en mayúscula
 * —«iOS» sale «Ios», «QA» sale «Qa»—. Lo que sí se reconstruye no está aquí:
 * «CHOFER» y «MANTENIMIENTO» los escribió alguien con el bloqueo de mayúsculas
 * puesto, y «Chofer» es mejor que gritarlo.
 */
const POSITION_ACRONYMS: Readonly<Record<string, string>> = {
  '2d': '2D',
  '3d': '3D',
  ceo: 'CEO',
  cfo: 'CFO',
  cro: 'CRO',
  dba: 'DBA',
  it: 'IT',
  ios: 'iOS',
  kao: 'KAO',
  kep: 'KEP',
  knu: 'KnU',
  latam: 'LATAM',
  lma: 'LMA',
  pm: 'PM',
  po: 'PO',
  qa: 'QA',
  rh: 'RH',
  sr: 'Sr.',
  ti: 'TI',
  ui: 'UI',
  ux: 'UX',
};

/**
 * El puesto de una persona, tal como se lee.
 *
 * El título lo manda el catálogo. Cuando el id no está en él se reconstruye
 * desde el propio id, porque lo que llegó a la pantalla era el identificador en
 * crudo —«ios-tech-lead-developer» en la ficha de alguien—, y un slug no es algo
 * que se le enseñe a una persona sobre sí misma.
 *
 * El caso ocurre por dos vías distintas y las dos son reales: un id que el
 * catálogo perdió —había uno así, `coordinador-de-desarrollo-profesional`— y un
 * catálogo que todavía no ha llegado, porque la configuración se sirve del caché
 * de Firestore antes que del servidor. La segunda se contesta con `undefined`
 * desde la pantalla, que sabe si la configuración cargó; ésta cubre la primera.
 */
/**
 * El puesto tal como se enseña, o `null` cuando no hay nada honesto que decir.
 *
 * Tres casos y no dos, que es lo que la hace existir en vez de estar escrita dos
 * veces:
 *
 *  - **Sin puesto registrado** → `null`. Cada pantalla decide qué hacer con eso:
 *    el perfil propio pone «Sin registrar» en su renglón, la ficha se salta la
 *    línea entera.
 *  - **Con puesto y sin catálogo todavía** → puntos suspensivos. La ficha guarda
 *    el id y el título vive en `config/catalogs`, que es otro documento; mientras
 *    viaja, el proveedor entrega los valores de fábrica —donde sólo existe «Sin
 *    asignar»— y pintar entonces enseñaba **el identificador en crudo**,
 *    «ios-tech-lead-developer», en la ficha de quien lo reportó.
 *  - **Con las dos cosas** → el título, vía `positionTitle`, que además cubre el
 *    id que el catálogo perdió.
 *
 * El del medio es el que se olvida, y por eso está aquí y no repetido en cada
 * pantalla que enseñe un puesto.
 */
export const positionLabel = (params: {
  readonly positionId: string | null;
  readonly positions: readonly { readonly id: string; readonly title: string }[];
  readonly configLoaded: boolean;
}): string | null => {
  if (params.positionId === null || params.positionId.length === 0) return null;
  if (!params.configLoaded) return '…';
  return positionTitle(params.positionId, params.positions);
};

export const positionTitle = (
  positionId: string,
  positions: readonly { readonly id: string; readonly title: string }[],
): string => {
  const known = positions.find((item) => item.id === positionId);
  if (known !== undefined) return known.title;

  return positionId
    .split('-')
    .filter((word) => word.length > 0)
    .map((word) => POSITION_ACRONYMS[word] ?? `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(' ');
};
